import { decodeEventLog, zeroAddress } from 'viem'
import {
  type Erc20Transfer,
  type Erc721Transfer,
  type Erc1155Transfer,
  type Hex,
  type LowerHex,
  toLowerHex,
} from '../../types'
import type { InternalTransaction } from '../../types/internalTransaction'
import type { Log } from '../../types/log'

type DecodeTokenTransferLogsReturnTypes = {
  erc20Transfers: Erc20Transfer[]
  erc721Transfers: Erc721Transfer[]
  erc1155Transfers: Erc1155Transfer[]
  inferredNativeTransfers: InternalTransaction[]
  logs: Log[]
}

type DecodedTokenTransferLog = {
  log: Log
  erc20Transfer?: Erc20Transfer
  erc721Transfer?: Erc721Transfer
  erc1155Transfers?: Erc1155Transfer[]
  inferredNativeTransfer?: InternalTransaction
  wrappedTransferIsInferred?: boolean
}

/**
 * ERC20 transfer の内容を重複判定用のキーに変換する。
 *
 * @param parameters.transfer - キーへ変換する ERC20 transfer
 * @returns コントラクト、送受信者、数量を連結したキー
 */
const createErc20TransferKey = ({
  transfer,
}: {
  transfer: Erc20Transfer
}): string =>
  [
    transfer.address,
    transfer.from,
    transfer.to,
    transfer.value.toString(),
  ].join('/')

/**
 * transaction receipt のログから token transfer と補完可能な native transfer を抽出する。
 *
 * @param logs - transaction receipt のログ
 * @param wrappedTokenAddresses - チェーンで許可された wrapped token address
 * @returns デコード済みログと各 token transfer
 */
export const decodeTokenTransferLogs = (
  logs: Log[],
  wrappedTokenAddresses: Set<LowerHex>,
): DecodeTokenTransferLogsReturnTypes => {
  const decodedLogs: Log[] = []
  const erc20Transfers: Erc20Transfer[] = []
  const erc721Transfers: Erc721Transfer[] = []
  const erc1155Transfers: Erc1155Transfer[] = []
  const inferredNativeTransfers: InternalTransaction[] = []
  const decodedTokenTransferLogs: DecodedTokenTransferLog[] = []
  for (const log of logs) {
    const decodedTokenTransferLog = decodeTokenTransferLog(
      log,
      wrappedTokenAddresses,
    )
    decodedTokenTransferLogs.push(decodedTokenTransferLog)
    const {
      log: decodedLog,
      erc721Transfer,
      erc1155Transfers: optionalErc1155Transfers,
      inferredNativeTransfer,
    } = decodedTokenTransferLog
    decodedLogs.push(decodedLog)
    if (erc721Transfer !== undefined) {
      erc721Transfers.push(erc721Transfer)
    }
    if (optionalErc1155Transfers !== undefined) {
      erc1155Transfers.push(...optionalErc1155Transfers)
    }
    if (inferredNativeTransfer !== undefined) {
      inferredNativeTransfers.push(inferredNativeTransfer)
    }
  }

  const actualErc20TransferCounts = new Map<string, number>()
  for (const decodedTokenTransferLog of decodedTokenTransferLogs) {
    if (
      decodedTokenTransferLog.erc20Transfer === undefined ||
      decodedTokenTransferLog.wrappedTransferIsInferred === true
    ) {
      continue
    }
    const key = createErc20TransferKey({
      transfer: decodedTokenTransferLog.erc20Transfer,
    })
    actualErc20TransferCounts.set(
      key,
      (actualErc20TransferCounts.get(key) ?? 0) + 1,
    )
  }
  for (const decodedTokenTransferLog of decodedTokenTransferLogs) {
    const erc20Transfer = decodedTokenTransferLog.erc20Transfer
    if (erc20Transfer === undefined) {
      continue
    }
    if (decodedTokenTransferLog.wrappedTransferIsInferred === true) {
      const key = createErc20TransferKey({ transfer: erc20Transfer })
      const actualTransferCount = actualErc20TransferCounts.get(key) ?? 0
      if (actualTransferCount > 0) {
        actualErc20TransferCounts.set(key, actualTransferCount - 1)
        continue
      }
    }
    erc20Transfers.push(erc20Transfer)
  }
  return {
    erc20Transfers,
    erc721Transfers,
    erc1155Transfers,
    inferredNativeTransfers,
    logs: decodedLogs,
  }
}

const erc20NftAddress = new Set<LowerHex>([
  '0xf5b0a3efb8e8e4c201e2a935f110eaaf3ffecb8d',
])

const noIndexErc721Address = new Set<LowerHex>([
  '0xda9f43015749056182352e9dc6d3ee0b6293d80a',
])

const decodeTokenTransferLog = (
  log: Log,
  wrappedTokenAddresses: Set<LowerHex>,
): DecodedTokenTransferLog => {
  // ERC20 形式の NFT token の場合、別で処理する
  if (erc20NftAddress.has(log.address)) {
    return decodeErc20NftTransferLog(log)
  }

  // NoIndex ERC721 の場合、別で処理する
  if (noIndexErc721Address.has(log.address)) {
    return decodeNoIndexLog(log)
  }

  // decode by ERC20
  const decodedByErc20 = decodeErc20TransferLog(log)
  if (decodedByErc20 !== undefined) {
    return decodedByErc20
  }

  // decode by WrappedToken
  if (wrappedTokenAddresses.has(log.address)) {
    const decodedByWrappedTokenDeposit = decodeWrappedTokenDepositLog(log)
    if (decodedByWrappedTokenDeposit !== undefined) {
      return {
        ...decodedByWrappedTokenDeposit,
        wrappedTransferIsInferred: true,
      }
    }
    const decodedByNoIndexedWrappedTokenDeposit =
      decodeNoIndexedWrappedTokenDepositLog(log)
    if (decodedByNoIndexedWrappedTokenDeposit !== undefined) {
      return {
        ...decodedByNoIndexedWrappedTokenDeposit,
        wrappedTransferIsInferred: true,
      }
    }
    const decodedByWrappedTokenWithdrawal = decodeWrappedTokenWithdrawalLog(log)
    if (decodedByWrappedTokenWithdrawal !== undefined) {
      return {
        ...decodedByWrappedTokenWithdrawal,
        wrappedTransferIsInferred: true,
      }
    }
    const decodedByNoIndexedWrappedTokenWithdrawal =
      decodeNoIndexedWrappedTokenWithdrawalLog(log)
    if (decodedByNoIndexedWrappedTokenWithdrawal !== undefined) {
      return {
        ...decodedByNoIndexedWrappedTokenWithdrawal,
        wrappedTransferIsInferred: true,
      }
    }
  }

  // decode by ERC721
  const decodedByErc721 = decodeErc721TransferLog(log)
  if (decodedByErc721 !== undefined) {
    return decodedByErc721
  }

  // decode by ERC1155
  const decodedByErc1155Single = decodeErc1155TransferSingleLog(log)
  if (decodedByErc1155Single !== undefined) {
    return decodedByErc1155Single
  }
  const decodedByErc1155Batch = decodeErc1155TransferBatchLog(log)
  if (decodedByErc1155Batch !== undefined) {
    return decodedByErc1155Batch
  }

  return { log }
}

const decodeErc20NftTransferLog = (
  log: Log,
): {
  log: Log
  erc20Transfer?: Erc20Transfer
  erc721Transfer?: Erc721Transfer
  erc1155Transfers?: Erc1155Transfer[]
} => {
  // decode by ERC20 as ERC721
  const decodedByErc20AsErc721 = decodeErc20TransferLogAsErc721Transfer(log)
  if (decodedByErc20AsErc721 !== undefined) {
    return decodedByErc20AsErc721
  }

  return { log }
}

const decodeNoIndexLog = (
  log: Log,
): {
  log: Log
  erc20Transfer?: Erc20Transfer
  erc721Transfer?: Erc721Transfer
  erc1155Transfers?: Erc1155Transfer[]
} => {
  // decode by NoIndex ERC721
  const decodedByNoIndexTransfer = decodeNoIndexErc721TransferLog(log)
  if (decodedByNoIndexTransfer !== undefined) {
    return decodedByNoIndexTransfer
  }

  return { log }
}

const erc20TransferSignature =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
const erc20TransferIndexedCount = 2

const decodeErc20TransferLog = (
  log: Log,
): { log: Log; erc20Transfer: Erc20Transfer } | undefined => {
  if (
    log.topics.length !== erc20TransferIndexedCount + 1 ||
    log.topics[0] !== erc20TransferSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.topics[2]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data.length !== 66
  ) {
    return undefined
  }

  const from = toLowerHex(`0x${log.topics[1].slice(26)}`)
  const to = toLowerHex(`0x${log.topics[2].slice(26)}`)
  const value = BigInt(log.data)

  log.eventName = 'Transfer'
  log.args = [
    {
      indexed: true,
      name: 'from',
      type: 'address',
      value: from,
    },
    {
      indexed: true,
      name: 'to',
      type: 'address',
      value: to,
    },
    {
      indexed: false,
      name: 'value',
      type: 'uint256',
      value,
    },
  ]

  return {
    log,
    erc20Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from,
      to,
      value,
    },
  }
}

const decodeErc20TransferLogAsErc721Transfer = (
  log: Log,
): { log: Log; erc721Transfer: Erc721Transfer } | undefined => {
  if (
    log.topics.length !== erc20TransferIndexedCount + 1 ||
    log.topics[0] !== erc20TransferSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.topics[2]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data.length !== 66
  ) {
    return undefined
  }

  const from = toLowerHex(`0x${log.topics[1].slice(26)}`)
  const to = toLowerHex(`0x${log.topics[2].slice(26)}`)
  const tokenId = BigInt(log.data)

  log.eventName = 'Transfer'
  log.args = [
    {
      indexed: true,
      name: 'from',
      type: 'address',
      value: from,
    },
    {
      indexed: true,
      name: 'to',
      type: 'address',
      value: to,
    },
    {
      indexed: true,
      name: 'tokenId',
      type: 'uint256',
      value: tokenId,
    },
  ]

  return {
    log,
    erc721Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from,
      to,
      tokenId,
    },
  }
}

const wrappedTokenDepositSignature =
  '0xe1fffcc4923d04b559f4d29a8bfc6cda04eb5b0d3c460751c2402c5c5cc9109c'
const wrappedTokenDepositIndexedCount = 1
const decodeWrappedTokenDepositLog = (
  log: Log,
): { log: Log; erc20Transfer: Erc20Transfer } | undefined => {
  if (
    log.topics.length !== wrappedTokenDepositIndexedCount + 1 ||
    log.topics[0] !== wrappedTokenDepositSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data.length !== 66
  ) {
    return undefined
  }

  const dst = toLowerHex(`0x${log.topics[1].slice(26)}`)
  const wad = BigInt(log.data)

  log.eventName = 'Deposit'
  log.args = [
    {
      indexed: true,
      name: 'dst',
      type: 'address',
      value: dst,
    },
    {
      indexed: false,
      name: 'wad',
      type: 'uint256',
      value: wad,
    },
  ]

  return {
    log,
    erc20Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from: zeroAddress,
      to: dst,
      value: wad,
    },
  }
}
const noIndexedWrappedTokenDepositIndexedCount = 0
const decodeNoIndexedWrappedTokenDepositLog = (
  log: Log,
): { log: Log; erc20Transfer: Erc20Transfer } | undefined => {
  if (
    log.topics.length !== noIndexedWrappedTokenDepositIndexedCount + 1 ||
    log.topics[0] !== wrappedTokenDepositSignature ||
    log.data.length !== 130
  ) {
    return undefined
  }

  const sender = toLowerHex(`0x${log.data.slice(26, 66)}`)
  const value = BigInt(`0x${log.data.slice(66, 130)}`)

  log.eventName = 'Deposit'
  log.args = [
    {
      indexed: true,
      name: 'sender',
      type: 'address',
      value: sender,
    },
    {
      indexed: false,
      name: 'value',
      type: 'uint256',
      value,
    },
  ]

  return {
    log,
    erc20Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from: zeroAddress,
      to: sender,
      value,
    },
  }
}

const wrappedTokenWithdrawalSignature =
  '0x7fcf532c15f0a6db0bd6d0e038bea71d30d808c7d98cb3bf7268a95bf5081b65'
const wrappedTokenWithdrawalIndexedCount = 1
const decodeWrappedTokenWithdrawalLog = (
  log: Log,
):
  | {
      log: Log
      erc20Transfer: Erc20Transfer
      inferredNativeTransfer: InternalTransaction
    }
  | undefined => {
  if (
    log.topics.length !== wrappedTokenWithdrawalIndexedCount + 1 ||
    log.topics[0] !== wrappedTokenWithdrawalSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data.length !== 66
  ) {
    return undefined
  }

  const src = toLowerHex(`0x${log.topics[1].slice(26)}`)
  const wad = BigInt(log.data)

  log.eventName = 'Withdrawal'
  log.args = [
    {
      indexed: true,
      name: 'src',
      type: 'address',
      value: src,
    },
    {
      indexed: false,
      name: 'wad',
      type: 'uint256',
      value: wad,
    },
  ]

  return {
    log,
    erc20Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from: src,
      to: zeroAddress,
      value: wad,
    },
    inferredNativeTransfer: {
      from: log.address,
      gas: 0n,
      isError: false,
      to: src,
      txHash: log.transactionHash,
      value: wad,
    },
  }
}
const noIndexedWrappedTokenWithdrawalIndexedCount = 0
const decodeNoIndexedWrappedTokenWithdrawalLog = (
  log: Log,
):
  | {
      log: Log
      erc20Transfer: Erc20Transfer
      inferredNativeTransfer: InternalTransaction
    }
  | undefined => {
  if (
    log.topics.length !== noIndexedWrappedTokenWithdrawalIndexedCount + 1 ||
    log.topics[0] !== wrappedTokenWithdrawalSignature ||
    log.data.length !== 130
  ) {
    return undefined
  }

  const sender = toLowerHex(`0x${log.data.slice(26, 66)}`)
  const value = BigInt(`0x${log.data.slice(66, 130)}`)

  log.eventName = 'Withdrawal'
  log.args = [
    {
      indexed: false,
      name: 'sender',
      type: 'address',
      value: sender,
    },
    {
      indexed: false,
      name: 'value',
      type: 'uint256',
      value: value,
    },
  ]

  return {
    log,
    erc20Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from: sender,
      to: zeroAddress,
      value,
    },
    inferredNativeTransfer: {
      from: log.address,
      gas: 0n,
      isError: false,
      to: sender,
      txHash: log.transactionHash,
      value,
    },
  }
}

const erc721TransferSignature =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef'
const erc721TransferIndexedCount = 3

const decodeErc721TransferLog = (
  log: Log,
): { log: Log; erc721Transfer: Erc721Transfer } | undefined => {
  if (
    log.topics.length !== erc721TransferIndexedCount + 1 ||
    log.topics[0] !== erc721TransferSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.topics[2]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data !== '0x'
  ) {
    return undefined
  }

  const from = toLowerHex(`0x${log.topics[1].slice(26)}`)
  const to = toLowerHex(`0x${log.topics[2].slice(26)}`)
  const tokenId = BigInt(log.topics[3])

  log.eventName = 'Transfer'
  log.args = [
    {
      indexed: true,
      name: 'from',
      type: 'address',
      value: from,
    },
    {
      indexed: true,
      name: 'to',
      type: 'address',
      value: to,
    },
    {
      indexed: true,
      name: 'tokenId',
      type: 'uint256',
      value: tokenId,
    },
  ]

  return {
    log,
    erc721Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from,
      to,
      tokenId,
    },
  }
}

const decodeNoIndexErc721TransferLog = (
  log: Log,
): { log: Log; erc721Transfer: Erc721Transfer } | undefined => {
  if (
    log.topics.length !== 1 ||
    log.topics[0] !== erc721TransferSignature ||
    log.data.length !== 192
  ) {
    return undefined
  }

  const from = toLowerHex(`0x${log.data.slice(26, 66)}`)
  const to = toLowerHex(`0x${log.data.slice(90, 130)}`)
  const tokenId = BigInt(log.data.slice(130))

  log.eventName = 'Transfer'
  log.args = [
    {
      indexed: true,
      name: 'from',
      type: 'address',
      value: from,
    },
    {
      indexed: true,
      name: 'to',
      type: 'address',
      value: to,
    },
    {
      indexed: true,
      name: 'tokenId',
      type: 'uint256',
      value: tokenId,
    },
  ]

  return {
    log,
    erc721Transfer: {
      address: log.address,
      logIndex: log.logIndex,
      from,
      to,
      tokenId,
    },
  }
}

const erc1155TransferSingleSignature =
  '0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62'
const erc1155TransferSingleIndexedCount = 3

const decodeErc1155TransferSingleLog = (
  log: Log,
): { log: Log; erc1155Transfers: Erc1155Transfer[] } | undefined => {
  if (
    log.topics.length !== erc1155TransferSingleIndexedCount + 1 ||
    log.topics[0] !== erc1155TransferSingleSignature ||
    log.topics[1]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.topics[2]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.topics[3]?.slice(0, 26) !== '0x000000000000000000000000' ||
    log.data.length !== 130
  ) {
    return undefined
  }

  const from = toLowerHex(`0x${log.topics[2].slice(26)}`)
  const to = toLowerHex(`0x${log.topics[3].slice(26)}`)
  const tokenId = BigInt(log.data.slice(0, 66))
  const value = BigInt(`0x${log.data.slice(66, 130)}`)

  log.eventName = 'TransferSingle'
  log.args = [
    {
      indexed: true,
      name: 'operator',
      type: 'address',
      value: `0x${log.topics[1].slice(26)}`,
    },
    {
      indexed: true,
      name: 'from',
      type: 'address',
      value: from,
    },
    {
      indexed: true,
      name: 'to',
      type: 'address',
      value: to,
    },
    {
      indexed: false,
      name: 'id',
      type: 'uint256',
      value: tokenId,
    },
    {
      indexed: false,
      name: 'value',
      type: 'uint256',
      value,
    },
  ]

  return {
    log,
    erc1155Transfers: [
      {
        address: log.address,
        logIndex: log.logIndex,
        from,
        to,
        tokenId,
        value,
      },
    ],
  }
}

const erc1155TransferBatchSignature =
  '0x4a39dc06d4c0dbc64b70af90fd698a233a518aa5d07e595d983b8c0526c8f7fb'
const erc1155TransferBatchIndexedCount = 3

const decodeErc1155TransferBatchLog = (
  log: Log,
): { log: Log; erc1155Transfers: Erc1155Transfer[] } | undefined => {
  if (
    log.topics.length !== erc1155TransferBatchIndexedCount + 1 ||
    log.topics[0] !== erc1155TransferBatchSignature
  ) {
    return undefined
  }

  try {
    const decodedLog = decodeEventLog({
      abi: [
        {
          anonymous: false,
          inputs: [
            {
              indexed: true,
              internalType: 'address',
              name: 'operator',
              type: 'address',
            },
            {
              indexed: true,
              internalType: 'address',
              name: 'from',
              type: 'address',
            },
            {
              indexed: true,
              internalType: 'address',
              name: 'to',
              type: 'address',
            },
            {
              indexed: false,
              internalType: 'uint256[]',
              name: 'ids',
              type: 'uint256[]',
            },
            {
              indexed: false,
              internalType: 'uint256[]',
              name: 'values',
              type: 'uint256[]',
            },
          ],
          name: 'TransferBatch',
          type: 'event',
        },
      ] as const,
      topics: log.topics as [Hex, ...Hex[]] | [],
      data: log.data,
    })

    const from = toLowerHex(decodedLog.args.from)
    const to = toLowerHex(decodedLog.args.to)

    log.eventName = decodedLog.eventName
    log.args = [
      {
        indexed: true,
        name: 'operator',
        type: 'address',
        value: decodedLog.args.operator,
      },
      {
        indexed: true,
        name: 'from',
        type: 'address',
        value: from,
      },
      {
        indexed: true,
        name: 'to',
        type: 'address',
        value: to,
      },
      {
        indexed: false,
        name: 'ids',
        type: 'uint256[]',
        value: decodedLog.args.ids,
      },
      {
        indexed: false,
        name: 'values',
        type: 'uint256[]',
        value: decodedLog.args.values,
      },
    ]

    return {
      log,
      erc1155Transfers: decodedLog.args.ids.map((id, index) => ({
        address: log.address,
        logIndex: log.logIndex,
        from,
        to,
        tokenId: id,
        value: decodedLog.args.values[index],
      })),
    }
  } catch (error) {
    console.debug(error)
    return undefined
  }
}
